import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEye,
    faEyeSlash,
    faMobileAlt,
    faCreditCard,
    faMoneyBillWave,
    faUniversity,
    faRedo,
    faLock,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

// Helper function for currency formatting (assuming KES or similar African currency)
const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency', 
        currency: 'KES', 
        minimumFractionDigits: 2,
    }).format(numericAmount).replace('KES', 'Ksh ');
};

const WithdrawalsAdminPage = () => {
    const [balance, setBalance] = useState(null);
    const [kraRunning, setKraRunning] = useState(0); // KRA Running Commission
    const [runningBalance, setRunningBalance] = useState(0); // Total Commission Balance
    const [amountPossibleToWithdraw, setAmountPossibleToWithdraw] = useState(0); // Calculated amount
    
    const [showBalance, setShowBalance] = useState(true);
    const [withdrawalAmount, setWithdrawalAmount] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [selectedMode, setSelectedMode] = useState(null);
    const [showMobileMoneyOptions, setShowMobileMoneyOptions] = useState(false);
    const [showCardPaymentOptions, setShowCardPaymentOptions] = useState(false);
    const [bankAccount, setBankAccount] = useState("");
    const [senderNames, setSenderNames] = useState("");
    const [bankCode, setBankCode] = useState("");
    const [banks, setBanks] = useState([]);
    const [selectedBank, setSelectedBank] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isOTPOpen, setIsOTPOpen] = useState(false);
    const [isOTPCardOpen, setIsOTPCardOpen] = useState(false);
    const [otp, setOtp] = useState(["", "", "", "", ""]);
    
    const otpInputsRef = useRef([]); // Ref for OTP inputs for better UX

    const token = localStorage.getItem("token");

    // Fetch Balance, Banks, and Commission Data
    useEffect(() => {
        if (token) {
            const headers = { Authorization: `Bearer ${token}` };

            // 1. Fetch Internal Balance
            axios
                .get(
                    `${StandardURL}/iNyumba/admin/househunter/fetchinyumbabalance`,
                    { headers }
                )
                .then((response) => {
                    if (response.data && response.data.response.length > 0) {
                        setBalance(response.data.response[0].balance);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching balance:", error);
                });

            // 2. Fetch Banks
            axios
                .get(`${StandardURL}/iNyumba/admin/bank/fetch`, { headers })
                .then((response) => {
                    setBanks(response.data.response);
                })
                .catch((error) => {
                    console.error("Error fetching banks:", error);
                });
                
            // 3. Fetch Commission Data (kra_running and running_balance)
            axios
                .get(`${StandardURL}/iNyumba/commissionlink/fetch/all/meadmin`, { headers })
                .then((response) => {
                    // --- REVISED LOGIC based on user-provided response structure ---
                    if (response.data && response.data.response && response.data.response.kra_running !== undefined) {
                        const data = response.data.response;
                        // Use parseFloat() to ensure numeric storage, handling potential negative strings
                        setKraRunning(parseFloat(data.kra_running) || 0);
                        setRunningBalance(parseFloat(data.running_balance) || 0);
                    } else if (response.data && Array.isArray(response.data.response) && response.data.response.length > 0) {
                        // Fallback: handle if it's an array with one item (as in the previous assumption)
                         const data = response.data.response[0];
                        setKraRunning(parseFloat(data.kra_running) || 0);
                        setRunningBalance(parseFloat(data.running_balance) || 0);
                    }
                    // -----------------------------------------------------------------
                })
                .catch((error) => {
                    console.error("Error fetching commission data:", error);
                });
        }
    }, [token]);

    // Calculate Amount Possible to Withdraw
    useEffect(() => {
        // Convert balance to number, handling null/string state from API
        const currentBalance = parseFloat(balance) || 0;
        const currentKraRunning = parseFloat(kraRunning) || 0; // Already parsed in fetch
        
        // Calculate: balance - kra_running
        const calculatedAmount = currentBalance - currentKraRunning;

        // Apply the condition: if negative, set to 0
        const finalAmount = calculatedAmount > 0 ? calculatedAmount : 0;

        // Note: withdrawalAmount state is not affected, as requested.
        setAmountPossibleToWithdraw(finalAmount);
    }, [balance, kraRunning]); // Recalculates when balance or kraRunning changes


    const handleBankSelect = (bankCode) => {
        setBankCode(bankCode);
        setSelectedBank(bankCode);
    };

    const handleWithdrawalSubmit = async (e) => {
        e.preventDefault();

        if (!token || !withdrawalAmount || !selectedMode) {
            toast.error("Withdrawal amount and mode are required.");
            return;
        }
        
        // Ensure withdrawal amount is valid and within the internal balance limit
        if (parseFloat(withdrawalAmount) <= 0 || (balance !== null && parseFloat(withdrawalAmount) > parseFloat(balance))) {
            toast.error("Invalid withdrawal amount or insufficient funds in internal balance.");
            return;
        }
        
        // Additional check against the 'Amount Possible to Withdraw'
         if (parseFloat(withdrawalAmount) > amountPossibleToWithdraw) {
            toast.error(`Withdrawal amount exceeds the calculated maximum possible withdrawal of ${formatCurrency(amountPossibleToWithdraw)}. Check KRA Commission.`);
            return;
        }


        setIsLoading(true);

        try {
            let response;

            if (selectedMode === "mobilemoney") {
                if (!phoneNumber || !selectedChannel) {
                    toast.error("Phone number and Mobile Money channel are required.");
                    setIsLoading(false);
                    return;
                }
                const requestData = {
                    amount: withdrawalAmount,
                    phone_number: phoneNumber,
                    channel: selectedChannel,
                };

                const jsonData = JSON.stringify(requestData);
                response = await axios.post(
                    `${StandardURL}/iNyumba/adminsettlementinitiated`,
                    jsonData,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (response.data.responseCode === 200) {
                    const message = response.data.response;
                    if (message.includes("Withdrawal has been blocked")) {
                        toast.info("Withdrawal has been blocked. Kindly contact admin.");
                    } else if (message.includes("Check your email for OTP")) {
                        toast.info("A one-time password has been sent to your email.");
                        setIsOTPOpen(true);
                    } else if (message.includes("insufficient funds")) {
                        toast.error(message);
                    } else if (response.data.responseCode === 201) {
                        toast.error(response.data.response);
                    }
                } else {
                    toast.error(response.data.response || "Mobile Money initiation failed.");
                }
            } else if (selectedMode === "cards") {
                if (!selectedBank || !bankAccount || !senderNames) {
                    toast.error("Bank, Account, and Sender Names are required for Card/Bank withdrawal.");
                    setIsLoading(false);
                    return;
                }

                const requestData = {
                    amount: withdrawalAmount,
                    bankaccount: bankAccount,
                    bankcode: bankCode,
                    sendernames: senderNames,
                };

                const jsonData = JSON.stringify(requestData);

                response = await axios.post(
                    `${StandardURL}/iNyumba/adminsettlementcardsinitiated`,
                    jsonData,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (response.data.responseCode === 200) {
                    const message = response.data.response;
                    if (message.includes("Withdrawal has been blocked")) {
                        toast.info("Withdrawal has been blocked. Kindly contact admin.");
                    } else if (message.includes("Check your email for OTP")) {
                        toast.info("A one-time password has been sent to your email.");
                        setIsOTPCardOpen(true);
                    } else if (message.includes("insufficient funds")) {
                        toast.error(message);
                    } else if (response.data.responseCode === 201) {
                        toast.error(response.data.response);
                    }
                } else {
                    handleResponseErrors(response.data.response);
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.response || "Error submitting withdrawal. Please try again.");
            console.error("Error submitting withdrawal:", error);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Functionality for response errors is kept intact
    const handleResponseErrors = (response) => {
        switch (response) {
            case "Hash mismatch error. Please check your data and try again.":
                toast.error("Hash mismatch error. Please verify your data.");
                break;
            case "Transaction reference exists. Please generate a unique reference.":
                toast.error("Reference exists. Please use a unique reference.");
                break;
            case "One or more parameters are missing.":
                toast.error(
                    "Some required parameters are missing. Please check and try again."
                );
                break;
            case "Request from unregistered IP address. Please ensure your IP is registered.":
                toast.error(
                    "Unregistered IP address. Please verify your network settings."
                );
                break;
            case "Insufficient funds in your iPay bulkpay account.":
                toast.error("Insufficient funds in your iPay bulkpay account.");
                break;
            default:
                toast.error("Bad request. Please check your input and try again.");
        }
    };

    // OTP handler (functionality preserved)
    const handleVerifyOTP = async () => {
        const otpCode = otp.join("");

        if (!otpCode || otpCode.length !== 5) {
            toast.error("Please enter a valid 5-digit OTP.");
            return;
        }

        const requestData = {
            amount: withdrawalAmount,
            phone_number: phoneNumber,
            channel: selectedChannel,
            otp: otpCode,
        };

        const jsonData = JSON.stringify(requestData);

        try {
            const response = await axios.post(
                `${StandardURL}/iNyumba/adminsettlementcompletesettle`,
                jsonData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.responseCode === 200) {
                const message = response.data.response;
                if (message.includes("Withdrawal has been blocked")) {
                    toast.error("Withdrawal has been blocked. Kindly contact admin.");
                } else if (message.includes("OTP limit of 5")) {
                    toast.error("Your account has reached the OTP limit of 5.");
                } else if (message === "Invalid OTP") {
                    toast.error(message);
                } else if (message.includes("B2C blocked request") || message.includes("B2C processing request")) {
                    toast.info(message);
                    closeModal();
                } else {
                    toast.success(message);
                    closeModal();
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.response || "OTP verification failed.");
        }
    };

    // Resend OTP handler (functionality preserved)
    const handleResendOTP = async () => {
        try {
            const response = await axios.patch(
                `${StandardURL}/iNyumba/adminsettlementotpresend`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.responseCode === 200) {
                toast.success(response.data.response);
            } else {
                toast.error(response.data.response);
            }
        } catch (error) {
            toast.error(error.response?.data?.response || "Failed to resend OTP.");
        }
    };
    
    // Card OTP verification (functionality preserved)
    const handleVerifyCardOTP = async () => {
        const otpCode = otp.join(""); 

        if (!otpCode || otpCode.length !== 5) {
            toast.error("Please enter a valid 5-digit OTP.");
            return;
        }

        const requestData = {
            amount: withdrawalAmount,
            bankaccount: bankAccount,
            bankcode: bankCode,
            sendernames: senderNames,
            otp: otpCode,
        };

        const jsonData = JSON.stringify(requestData);

        try {
            const response = await axios.post(
                `${StandardURL}/iNyumba/adminsettlementcardscompletesettle`,
                jsonData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.responseCode === 200) {
                const message = response.data.response;
                if (message.includes("Withdrawal has been blocked")) {
                    toast.error("Withdrawal has been blocked. Kindly contact admin.");
                } else if (message.includes("OTP limit of 5")) {
                    toast.error("Your account has reached the OTP limit of 5.");
                } else if (message === "Invalid OTP") {
                    toast.error(message);
                } else if (message.includes("B2C blocked request") || message.includes("B2C processing request")) {
                    toast.info(message);
                    closeModal();
                } else {
                    toast.success(message);
                    closeModal();
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.response || "Card OTP verification failed.");
        }
    };

    // Resend Card OTP handler (functionality preserved)
    const handleResendCardOTP = async () => {
        try {
            const response = await axios.patch(
                `${StandardURL}/iNyumba/adminsettlementcardsotpresend`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.responseCode === 200) {
                toast.success(response.data.response);
            } else {
                toast.error(response.data.response);
            }
        } catch (error) {
            toast.error(error.response?.data?.response || "Failed to resend Card OTP.");
        }
    };


    const closeModal = () => {
        setIsOTPOpen(false);
        setIsOTPCardOpen(false);
        setOtp(["", "", "", "", ""]);
        setWithdrawalAmount("");
        setPhoneNumber("");
        setSelectedChannel(null);
        setShowMobileMoneyOptions(false);
        setShowCardPaymentOptions(false);
        setSelectedMode(null);
        setBankCode("");
        setSelectedBank("");
        // No change to balance states, allowing them to refresh from API on next load/trigger
    };

    const toggleShowBalance = () => {
        setShowBalance(!showBalance);
    };

    const handleModeSelection = (mode) => {
        setSelectedMode(mode);

        if (mode === "mobilemoney") {
            setShowMobileMoneyOptions(true);
            setShowCardPaymentOptions(false);
        } else if (mode === "cards") {
            setShowCardPaymentOptions(true);
            setShowMobileMoneyOptions(false);
        } else {
            setShowMobileMoneyOptions(false);
            setShowCardPaymentOptions(false);
        }
        // Reset mode-specific fields when switching
        setWithdrawalAmount("");
        setPhoneNumber("");
        setSelectedChannel(null);
        setBankAccount("");
        setSenderNames("");
        setBankCode("");
        setSelectedBank("");
    };

    const handleMobileMoneyClick = (channel) => {
        setSelectedChannel(channel);
    };

    // Enhanced OTP Change Handler
    const handleOtpChange = (index, value) => {
        if (/^\d*$/.test(value)) {
            setOtp((prevOtp) => {
                const newOtp = [...prevOtp];
                newOtp[index] = value.slice(-1); // Ensure only one digit
                return newOtp;
            });

            // Auto-focus to the next input field
            if (value && index < otp.length - 1) {
                otpInputsRef.current[index + 1]?.focus();
            }
        }
    };
    
    // Enhanced OTP Key Down Handler for backspace
    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            // Auto-focus to the previous input field on backspace if current field is empty
            otpInputsRef.current[index - 1]?.focus();
        }
    };

    const balanceColor = balance === null || parseFloat(balance) === 0 ? 'text-gray-500' : (parseFloat(balance) > 0 ? 'text-green-600' : 'text-red-600');

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-3xl mx-auto bg-white shadow-2xl rounded-xl p-6 lg:p-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-6 flex items-center border-b pb-4">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="text-indigo-600 mr-3" /> Admin Fund Withdrawals
                        </h1>

                        {/* Balance Display Card */}
                        <div className="p-5 mb-8 rounded-xl border border-gray-200 bg-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-500">Available Internal Balance:</span>
                                <button
                                    onClick={toggleShowBalance}
                                    className="p-1 rounded-full text-indigo-500 hover:text-indigo-700 transition focus:outline-none"
                                >
                                    <FontAwesomeIcon
                                        icon={showBalance ? faEyeSlash : faEye}
                                        className="w-5 h-5"
                                    />
                                </button>
                            </div>
                            <span className={`font-extrabold text-4xl mt-1 block ${balanceColor}`}>
                                {showBalance ? (balance !== null ? formatCurrency(balance) : "Ksh N/A") : "Ksh XXXXXX.XX"}
                            </span>
                             {balance !== null && parseFloat(balance) < 0 && (
                                <p className="text-sm text-red-600 mt-2">Warning: Internal Balance is negative.</p>
                            )}

                            {/* NEW: Commission and Possible Withdrawal Section */}
                            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm font-medium">
                                <div>
                                    <span className="text-gray-500 block">KRA Running Commission:</span>
                                    <span className="font-extrabold block text-orange-600 text-lg">
                                        {showBalance ? formatCurrency(kraRunning) : "Ksh XXXXXX.XX"}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500 block">Total Commission Balance:</span>
                                    <span className="font-extrabold block text-purple-600 text-lg">
                                        {showBalance ? formatCurrency(runningBalance) : "Ksh XXXXXX.XX"}
                                    </span>
                                </div>
                                <div className="col-span-2 mt-4 pt-4 border-t border-indigo-100">
                                    <span className="text-gray-500 block text-lg">Amount Possible to Withdraw:</span>
                                    <span className={`font-extrabold block text-2xl ${amountPossibleToWithdraw === 0 ? 'text-red-500' : 'text-indigo-600'}`}>
                                        {showBalance ? formatCurrency(amountPossibleToWithdraw) : "Ksh XXXXXX.XX"}
                                    </span>
                                    {amountPossibleToWithdraw === 0 && (
                                        <p className="text-xs text-red-500 mt-1">Note: KRA running commission is equal to or greater than the available internal balance. Withdrawal amount must be KSh 0.00.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <form onSubmit={handleWithdrawalSubmit}>
                             {/* Withdrawal Mode Selection */}
                            <div className="mb-6">
                                <label className="block mb-3 text-lg font-bold text-gray-700">Select Withdrawal Method</label>
                                <div className="grid grid-cols-2 gap-4">
                                    <button
                                        type="button"
                                        className={`flex flex-col items-center p-4 rounded-lg border-2 transition duration-150 ${
                                            selectedMode === "mobilemoney"
                                                ? "border-indigo-600 bg-indigo-50 text-indigo-700 shadow-md"
                                                : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                                        }`}
                                        onClick={() => handleModeSelection("mobilemoney")}
                                    >
                                        <FontAwesomeIcon icon={faMobileAlt} className="w-6 h-6 mb-2" />
                                        <span className="font-bold text-sm sm:text-base">Mobile Money</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`flex flex-col items-center p-4 rounded-lg border-2 transition duration-150 ${
                                            selectedMode === "cards"
                                                ? "border-indigo-600 bg-indigo-50 text-indigo-700 shadow-md"
                                                : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                                        }`}
                                        onClick={() => handleModeSelection("cards")}
                                    >
                                        <FontAwesomeIcon icon={faCreditCard} className="w-6 h-6 mb-2" />
                                        <span className="font-bold text-sm sm:text-base">Bank/Card Transfer</span>
                                    </button>
                                </div>
                            </div>
                            
                            <div className="p-5 border border-gray-200 rounded-xl bg-gray-50 mb-6">
                                {/* Mobile Money Options */}
                                {showMobileMoneyOptions && (
                                    <div className="animate-in fade-in duration-300">
                                        <h3 className="text-xl font-semibold text-gray-800 mb-4">Mobile Money Details</h3>
                                        
                                        <label className="block mb-2 font-semibold text-gray-700">Select Channel</label>
                                        <div className="flex space-x-4 mb-4">
                                            {/* M-Pesa Button */}
                                            <button
                                                type="button"
                                                className={`py-2 px-4 rounded-full font-semibold border transition ${
                                                    selectedChannel === "mpesa"
                                                        ? "bg-indigo-600 text-white border-indigo-600"
                                                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                                                }`}
                                                onClick={() => handleMobileMoneyClick("mpesa")}
                                            >
                                                M-Pesa
                                            </button>
                                            {/* Airtel Money Button */}
                                            <button
                                                type="button"
                                                className={`py-2 px-4 rounded-full font-semibold border transition ${
                                                    selectedChannel === "airtelmoney"
                                                        ? "bg-indigo-600 text-white border-indigo-600"
                                                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                                                }`}
                                                onClick={() => handleMobileMoneyClick("airtelmoney")}
                                            >
                                                Airtel Money
                                            </button>
                                        </div>

                                        <label className="block mb-2 font-semibold text-gray-700">Amount (KES)</label>
                                        <input
                                            type="number"
                                            value={withdrawalAmount}
                                            onChange={(e) => setWithdrawalAmount(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:ring-indigo-500 focus:border-indigo-500 transition"
                                            placeholder={`Max: ${formatCurrency(amountPossibleToWithdraw)}`}
                                            min="1"
                                        />
                                        
                                        <label className="block mb-2 font-semibold text-gray-700">Phone Number</label>
                                        <input
                                            type="tel"
                                            value={phoneNumber}
                                            onChange={(e) => setPhoneNumber(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:ring-indigo-500 focus:border-indigo-500 transition"
                                            placeholder="e.g., 2547XXXXXXXX"
                                        />
                                    </div>
                                )}

                                {/* Card/Bank Payment Options */}
                                {showCardPaymentOptions && (
                                    <div className="animate-in fade-in duration-300">
                                        <h3 className="text-xl font-semibold text-gray-800 mb-4">Bank Transfer Details</h3>
                                        
                                        <label className="block mb-2 font-semibold text-gray-700">Amount (KES)</label>
                                        <input
                                            type="number"
                                            value={withdrawalAmount}
                                            onChange={(e) => setWithdrawalAmount(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:ring-indigo-500 focus:border-indigo-500 transition"
                                            placeholder={`Max: ${formatCurrency(amountPossibleToWithdraw)}`}
                                            min="1"
                                        />
                                        
                                        <label className="block mb-2 font-semibold text-gray-700">Recipient Names</label>
                                        <input
                                            type="text"
                                            value={senderNames}
                                            onChange={(e) => setSenderNames(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:ring-indigo-500 focus:border-indigo-500 transition"
                                            placeholder="Full name on bank account"
                                        />
                                        
                                        <label className="block mb-2 font-semibold text-gray-700">Bank Account Number</label>
                                        <input
                                            type="text"
                                            value={bankAccount}
                                            onChange={(e) => setBankAccount(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md p-2 mb-4 focus:ring-indigo-500 focus:border-indigo-500 transition"
                                            placeholder="Bank account number"
                                        />
                                        
                                        <label className="block mb-2 font-semibold text-gray-700">Select Bank</label>
                                        <div className="relative">
                                            <select
                                                value={selectedBank}
                                                onChange={(e) => handleBankSelect(e.target.value)}
                                                className="w-full border border-gray-300 rounded-md p-2 appearance-none focus:ring-indigo-500 focus:border-indigo-500 transition bg-white pr-8"
                                            >
                                                <option value="" disabled>-- Select Bank --</option>
                                                {banks.map((bank) => (
                                                    <option key={bank.bankcode} value={bank.bankcode}>
                                                        {bank.bankname}
                                                    </option>
                                                ))}
                                            </select>
                                            <FontAwesomeIcon icon={faUniversity} className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isLoading || !selectedMode || (selectedMode === "mobilemoney" && (!phoneNumber || !selectedChannel)) || (selectedMode === "cards" && (!bankAccount || !senderNames || !selectedBank))}
                                className={`w-full flex items-center justify-center py-3 px-4 rounded-md font-bold transition duration-300 ease-in-out ${
                                    isLoading ? "bg-gray-500 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
                                }`}
                            >
                                {isLoading ? (
                                    <>
                                        <Oval height={20} width={20} color="white" secondaryColor="white" visible={true} ariaLabel="loading-indicator" />
                                        <span className="ml-2">Processing Request...</span>
                                    </>
                                ) : (
                                    "Initiate Withdrawal"
                                )}
                            </button>
                        </form>
                    </div>
                </div>

                {/* OTP MODAL (Mobile Money) */}
                {isOTPOpen && (
                    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60 transition duration-300">
                        <div className="bg-white p-8 rounded-xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-300">
                            <h2 className="text-2xl font-bold mb-3 text-gray-800 flex items-center">
                                <FontAwesomeIcon icon={faLock} className="mr-2 text-indigo-500" /> Verify Mobile Money
                            </h2>
                            <p className="text-gray-600 mb-6 text-sm">
                                Please enter the 5-digit OTP sent to your registered email address to complete the **Mobile Money** withdrawal.
                            </p>
                            <div className="flex justify-between space-x-2 mb-6">
                                {otp.map((digit, index) => (
                                    <input
                                        ref={(el) => (otpInputsRef.current[index] = el)}
                                        key={index}
                                        type="tel"
                                        maxLength="1"
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        className="border-2 border-gray-300 text-center rounded-lg p-3 w-10 text-xl font-mono focus:border-indigo-500 focus:ring-indigo-500 transition"
                                    />
                                ))}
                            </div>
                            <div className="flex flex-col space-y-3">
                                <button
                                    onClick={handleVerifyOTP}
                                    className="bg-indigo-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition focus:outline-none"
                                >
                                    Verify OTP
                                </button>
                                <button
                                    onClick={handleResendOTP}
                                    className="bg-green-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-green-600 transition focus:outline-none flex items-center justify-center"
                                >
                                    <FontAwesomeIcon icon={faRedo} className="mr-2 w-4 h-4" /> Resend OTP
                                </button>
                                <button
                                    onClick={closeModal}
                                    className="bg-gray-300 text-gray-800 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 transition focus:outline-none"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* OTP MODAL (Cards/Bank) */}
                {isOTPCardOpen && (
                    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60 transition duration-300">
                        <div className="bg-white p-8 rounded-xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-300">
                             <h2 className="text-2xl font-bold mb-3 text-gray-800 flex items-center">
                                <FontAwesomeIcon icon={faLock} className="mr-2 text-indigo-500" /> Verify Bank Transfer
                            </h2>
                            <p className="text-gray-600 mb-6 text-sm">
                                Please enter the 5-digit OTP sent to your registered email address to complete the **Bank Transfer** withdrawal.
                            </p>
                            <div className="flex justify-between space-x-2 mb-6">
                                {otp.map((digit, index) => (
                                    <input
                                        ref={(el) => (otpInputsRef.current[index] = el)}
                                        key={index}
                                        type="tel"
                                        maxLength="1"
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        className="border-2 border-gray-300 text-center rounded-lg p-3 w-10 text-xl font-mono focus:border-indigo-500 focus:ring-indigo-500 transition"
                                    />
                                ))}
                            </div>
                            <div className="flex flex-col space-y-3">
                                <button
                                    onClick={handleVerifyCardOTP}
                                    className="bg-indigo-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition focus:outline-none"
                                >
                                    Verify OTP
                                </button>
                                <button
                                    onClick={handleResendCardOTP}
                                    className="bg-green-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-green-600 transition focus:outline-none flex items-center justify-center"
                                >
                                    <FontAwesomeIcon icon={faRedo} className="mr-2 w-4 h-4" /> Resend OTP
                                </button>
                                <button
                                    onClick={closeModal}
                                    className="bg-gray-300 text-gray-800 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 transition focus:outline-none"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </AuthGuard>
        </EmailGuard>
    );
};

export default WithdrawalsAdminPage;